import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import {
    Autocomplete, Container, Paper, Typography, Select, MenuItem, FormControl, InputLabel,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, CircularProgress, Box, Card, CardContent,
    OutlinedInput, Checkbox, ListItemText, Button, TextField
} from '@mui/material';

const ModelHoursReport = () => {
    const [reportData, setReportData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [groupBy, setGroupBy] = useState('models_total_hours'); // Default grouping
    const [error, setError] = useState(null);
    const [availableReports, setAvailableReports] = useState([]);
    const [selectedReports, setSelectedReports] = useState([]);
    const [availableModels, setAvailableModels] = useState([]);
    const [selectedModels, setSelectedModels] = useState([]);

    const container = document.getElementById('model-hours-report-container');
    const API_ENDPOINT = container ? container.dataset.apiEndpoint : '/api/reports/model-hours';
    const apiToken = container ? container.dataset.apiToken : null;

    useEffect(() => {
        fetchReportData();
        // eslint-disable-next-line
    }, [groupBy, selectedReports, selectedModels]);

    /** Keeps MUI multi-select values as numeric IDs for API filters and checkbox state. */
    const normalizeMultiSelectValue = (value) => {
        const rawValue = Array.isArray(value) ? value : String(value).split(',');

        return rawValue
            .filter((item) => item !== '')
            .map((item) => Number(item));
    };

    const fetchReportData = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await axios.get(API_ENDPOINT, {
                params: { group_by: groupBy, reports: selectedReports, models: selectedModels },
                headers: {
                    'Authorization': 'Bearer ' + apiToken,
                },
            });
            // Response now contains both data and availableReports
            setReportData(response.data.data);
            setAvailableReports(response.data.availableReports);
            setAvailableModels(response.data.availableModels || []);
        } catch (err) {
            console.error("Error fetching report data:", err);
            setError(err.response ? err.response.data.error || 'Failed to fetch data' : 'Failed to fetch data');
            setReportData(null);
        }
        setLoading(false);
    };

    const handleGroupByChange = (event) => {
        setLoading(true);
        setGroupBy(event.target.value);
    };

    const handleReportsChange = (event) => {
        setSelectedReports(normalizeMultiSelectValue(event.target.value));
    };

    /** Keeps the searchable model picker in sync with the numeric model IDs required by the API. */
    const handleModelsChange = (_, value) => {
        setSelectedModels(value.map((model) => model.id));
    };

    const handleExport = async () => {
        try {
            const params = { group_by: groupBy, reports: selectedReports, models: selectedModels, export: 1 };
            const response = await axios.get(API_ENDPOINT, {
                params,
                headers: {
                    'Authorization': 'Bearer ' + apiToken,
                },
                responseType: 'blob',
            });
            // Create a link to download the file
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'model_hours_report.xlsx');
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (err) {
            setError('Failed to export Excel file');
        }
    };

    const renderGroupedData = () => {
        if (!reportData) return <Typography>No data available.</Typography>;
        const showDeviceColumn = groupBy === 'models_by_flux';

        switch (groupBy) {
            case 'models_total_hours':
                return (
                    <TableContainer component={Paper} sx={{ mt: 2 }}>
                        <Table aria-label="models total hours table">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Model Name</TableCell>
                                    <TableCell align="right">Total Hours</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {reportData.map((row, index) => (
                                    <TableRow key={index}>
                                        <TableCell component="th" scope="row">
                                            {row.model_name}
                                        </TableCell>
                                        <TableCell align="right">{parseFloat(row.total_hours).toFixed(2)}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                );
            case 'models_by_device':
            case 'models_by_group':
            case 'models_by_flux':
                const groupTitle = groupBy.replace('models_by_', '').replace('_', ' ').toUpperCase();
                return (
                    <Box sx={{ mt: 2 }}>
                        {Object.entries(reportData).map(([groupKey, models], groupIndex) => (
                            <Card key={groupIndex} sx={{ mb: 2 }}>
                                <CardContent>
                                    <Typography variant="h6" gutterBottom>{`${groupTitle}: ${groupKey}`}</Typography>
                                    <TableContainer component={Paper}>
                                        <Table size="small" aria-label={`models for ${groupKey}`}>
                                            <TableHead>
                                                <TableRow>
                                                    {showDeviceColumn && <TableCell>Device</TableCell>}
                                                    <TableCell>Model Name (Client)</TableCell>
                                                    <TableCell align="right">Total Hours</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {models && models.map((model, modelIndex) => (
                                                    <TableRow key={modelIndex}>
                                                        {showDeviceColumn && (
                                                            <TableCell>{model.device_name || 'N/A'}</TableCell>
                                                        )}
                                                        <TableCell component="th" scope="row">
                                                            {model.model_name} <span style={{color:'#888', fontSize:'0.95em'}}>({model.client_name})</span>
                                                        </TableCell>
                                                        <TableCell align="right">{parseFloat(model.total_hours).toFixed(2)}</TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </CardContent>
                            </Card>
                        ))}
                    </Box>
                );
            default:
                return <Typography>Invalid grouping selected.</Typography>;
        }
    };

    return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            <Paper sx={{ p: 3 }}>
                <Typography variant="h4" gutterBottom component="div">
                    Model Hours Report
                </Typography>
                <Button
                    variant="contained"
                    color="primary"
                    sx={{ mb: 2 }}
                    onClick={handleExport}
                    disabled={loading}
                >
                    Export to Excel
                </Button>

                <FormControl fullWidth sx={{ mb: 3 }}>
                    <InputLabel id="reports-multi-label">Select Report(s)</InputLabel>
                    <Select
                        labelId="reports-multi-label"
                        id="reports-multi-select"
                        multiple
                        value={selectedReports}
                        onChange={handleReportsChange}
                        input={<OutlinedInput label="Select Report(s)" />}
                        renderValue={(selected) =>
                            availableReports
                                .filter(r => selected.includes(r.id))
                                .map(r => r.name)
                                .join(', ')
                        }
                    >
                        {availableReports.map((report) => (
                            <MenuItem key={report.id} value={report.id}>
                                <Checkbox checked={selectedReports.indexOf(report.id) > -1} />
                                <ListItemText primary={report.name} />
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <FormControl fullWidth sx={{ mb: 3, mt:1 }}>
                    <InputLabel id="group-by-label">Group By</InputLabel>
                    <Select
                        labelId="group-by-label"
                        id="group-by-select"
                        value={groupBy}
                        label="Group By"
                        onChange={handleGroupByChange}
                    >
                        <MenuItem value="models_total_hours">Total Hours per Model</MenuItem>
                        <MenuItem value="models_by_device">Models by Device</MenuItem>
                        <MenuItem value="models_by_group">Models by Item Group</MenuItem>
                        <MenuItem value="models_by_flux">Models by Flux</MenuItem>
                    </Select>
                </FormControl>

                <Autocomplete
                    multiple
                    disableCloseOnSelect
                    options={availableModels}
                    value={availableModels.filter((model) => selectedModels.includes(model.id))}
                    onChange={handleModelsChange}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    getOptionLabel={(option) => option.name || ''}
                    renderOption={(props, option, { selected }) => (
                        <li {...props}>
                            <Checkbox
                                sx={{ mr: 1 }}
                                checked={selected}
                            />
                            {option.name}
                        </li>
                    )}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Select Model(s)"
                            placeholder="Type to filter models"
                        />
                    )}
                    sx={{ mb: 3 }}
                />

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
                        <CircularProgress />
                    </Box>
                ) : error ? (
                     <Typography color="error" sx={{ mt: 2 }}>Error: {error}</Typography>
                ) : (
                    renderGroupedData()
                )}
            </Paper>
        </Container>
    );
};

if (document.getElementById('model-hours-report-container')) {
    const container = document.getElementById('model-hours-report-container');
    ReactDOM.render(<ModelHoursReport />, container);
}

export default ModelHoursReport;

